<template>
  <modal
    :show="isVisible"
    :title="t('Resumo das Regras de Pagamento')"
    size="large"
    elem-id="modal_payment-rules-summary"
    :closeLabel="t('Fechar')"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div class="summary-modal-content">
        <!-- Loading State -->
        <div v-if="isLoading" class="loading-container">
          <spinner />
          <p>{{ t('Carregando regras...') }}</p>
        </div>

        <!-- Content when loaded -->
        <template v-else>
          <!-- Summary Cards -->
          <div class="summary-header">
            <div class="summary-stat">
              <i class="pi pi-users"></i>
              <span class="stat-value">{{ summaryStats.totalColaboradores }}</span>
              <span class="stat-label">{{ t('colaboradores') }}</span>
            </div>
            <div class="summary-divider"></div>
            <div class="summary-stat">
              <i class="pi pi-list"></i>
              <span class="stat-value">{{ summaryStats.totalRules }}</span>
              <span class="stat-label">{{ t('regras ativas') }}</span>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="groupedData.length === 0" class="empty-state">
            <i class="pi pi-inbox"></i>
            <h3>{{ t('Nenhuma regra ativa encontrada') }}</h3>
            <p>{{ t('Não há regras de pagamento ativas para as franquias selecionadas no filtro.') }}</p>
          </div>

          <!-- Colaborador-Centered List -->
          <div v-else class="colaboradores-list">
            <div
              v-for="colaborador in groupedData"
              :key="colaborador.id"
              class="colaborador-card"
            >
              <!-- Colaborador Header -->
              <div class="colaborador-header">
                <div class="colaborador-avatar">
                  <i class="pi pi-user"></i>
                </div>
                <div class="colaborador-info">
                  <span class="colaborador-name">{{ colaborador.name }}</span>
                  <span class="colaborador-meta">
                    {{ colaborador.rules.length }} {{ colaborador.rules.length === 1 ? t('regra') : t('regras') }}
                  </span>
                </div>
              </div>

              <!-- Rules List -->
              <div class="rules-list">
                <div
                  v-for="rule in colaborador.rules"
                  :key="rule.id"
                  class="rule-item"
                >
                  <div class="rule-main">
                    <div class="rule-type">
                      <i :class="getRuleTypeIcon(rule.rule_type)"></i>
                      <span>{{ getRuleTypeLabel(rule.rule_type) }}</span>
                    </div>
                    <div class="rule-value">
                      {{ formatAmount(rule) }}
                    </div>
                  </div>
                  <div class="rule-details">
                    <span class="rule-franchise">
                      <i class="pi pi-building"></i>
                      {{ rule.franchise_name }}
                    </span>
                    <span class="rule-frequency">
                      {{ getFrequencyLabel() }}
                    </span>
                    <status-tag
                      v-if="hasCommissionStructure(rule.meta)"
                      :value="getStructureLabel(rule.meta)"
                      :severity="getStructureSeverity(rule.meta)"
                      class="structure-tag"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePaymentRules } from '@/composables/usePaymentRules';
import { permissionsService } from '@/services/permissionsService';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface Bracket {
  min: number;
  max: number | null;
  /** Rate stored as fraction (0..1). PRESERVED. */
  rate: number;
}

interface CommissionStructure {
  type?: 'simple' | 'tiered' | 'progressive';
  brackets?: Bracket[];
}

interface RuleMeta {
  commission_structure?: CommissionStructure;
  period_type?: string;
}

interface PaymentRule {
  id: Id;
  user_id: Id;
  franchise_id: Id;
  rule_type?: string;
  /** Amount semantics depend on rule_type — fraction (0..1) for commission, currency for fixed. PRESERVED. */
  amount?: number | string;
  meta?: string | RuleMeta | null;
  user_name?: string;
  franchise_name?: string;
  frequency?: string;
  active?: boolean | number | string;
}

interface ColaboradorGroup {
  id: Id;
  name: string;
  rules: PaymentRule[];
}

type TagSeverity = 'secondary' | 'info' | 'warning' | 'success' | 'danger' | 'contrast';

const { t, n } = useI18n();
const { listRules, getRuleTypeLabel, getFrequencyLabel } = usePaymentRules();

// State
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const rules = ref<PaymentRule[]>([]);

/**
 * Get franchise name from permissionsService
 */
const getFranchiseName = (franchiseId: Id | undefined): string => {
  if (!franchiseId) return '';

  try {
    const franchises = permissionsService.getFranquias() as Record<string, { Dados?: { Nome?: string } }> | null | undefined;
    const franchiseIdStr = String(franchiseId);

    if (franchises && franchises[franchiseIdStr]) {
      return franchises[franchiseIdStr].Dados?.Nome || '';
    }

    return '';
  } catch (err) {
    const e = err as Error;
    log('PaymentRulesSummaryModal', 'getFranchiseName', 'ERROR', { franchiseId, error: e.message });
    return '';
  }
};

/**
 * Get employee name from permissionsService
 */
const getEmployeeName = (userId: Id | undefined, franchiseId: Id | undefined): string => {
  if (!userId || !franchiseId) return '';

  try {
    const employees = permissionsService.getFuncionariosByFranquiaID(Number(franchiseId)) as Record<string, { funcionario_nome?: string }> | null | undefined;

    if (employees && employees[String(userId)]) {
      return employees[String(userId)].funcionario_nome || '';
    }

    return '';
  } catch (err) {
    const e = err as Error;
    log('PaymentRulesSummaryModal', 'getEmployeeName', 'ERROR', { userId, franchiseId, error: e.message });
    return '';
  }
};

/**
 * Group rules by colaborador (user-centered)
 * Creates structure: Colaborador > Rules[] (with franchise info in each rule)
 */
const groupRulesByColaborador = (rulesArray: PaymentRule[]): ColaboradorGroup[] => {
  const colaboradorMap: Record<string, ColaboradorGroup> = {};

  rulesArray.forEach(rule => {
    const visitorKey = `${rule.user_id}`;

    if (!colaboradorMap[visitorKey]) {
      const employeeName = getEmployeeName(rule.user_id, rule.franchise_id)
        || rule.user_name
        || t('Colaborador') + ' ' + rule.user_id;

      colaboradorMap[visitorKey] = {
        id: rule.user_id,
        name: employeeName,
        rules: []
      };
    }

    // Enrich rule with franchise name
    const franchiseName = getFranchiseName(rule.franchise_id)
      || rule.franchise_name
      || t('Franquia') + ' ' + rule.franchise_id;

    colaboradorMap[visitorKey].rules.push({
      ...rule,
      franchise_name: franchiseName
    });
  });

  // Convert to array and sort by name
  return Object.values(colaboradorMap)
    .sort((a, b) => a.name.localeCompare(b.name));
};

// Computed: Group rules by colaborador (user-centered view)
const groupedData = computed<ColaboradorGroup[]>(() => {
  return groupRulesByColaborador(rules.value);
});

// Computed: Summary statistics
const summaryStats = computed(() => {
  return {
    totalColaboradores: groupedData.value.length,
    totalRules: rules.value.length
  };
});

/**
 * Get rule type icon
 */
const getRuleTypeIcon = (ruleType: string | undefined): string => {
  const icons: Record<string, string> = {
    'fixed': 'pi pi-wallet',
    'commission_over_gross_revenue_executed': 'pi pi-percentage',
    'commission_over_gross_revenue_sold_remotely': 'pi pi-phone',
    'commission_over_gross_revenue_sold_in_loco': 'pi pi-map-marker'
  };
  return (ruleType && icons[ruleType]) || 'pi pi-circle';
};

const parseMeta = (meta: string | RuleMeta | null | undefined): RuleMeta | null => {
  if (!meta) return null;
  try {
    return typeof meta === 'string' ? (JSON.parse(meta) as RuleMeta) : meta;
  } catch (e) {
    return null;
  }
};

/**
 * Check if rule has commission structure
 */
const hasCommissionStructure = (meta: string | RuleMeta | null | undefined): boolean => {
  const parsed = parseMeta(meta);
  return !!parsed?.commission_structure?.type;
};

/**
 * Get structure label from meta field
 */
const getStructureLabel = (meta: string | RuleMeta | null | undefined): string => {
  const parsed = parseMeta(meta);
  if (parsed?.commission_structure?.type === 'tiered') return t('Escalonado');
  if (parsed?.commission_structure?.type === 'progressive') return t('Progressivo');
  return t('Simples');
};

/**
 * Get structure severity for Tag component
 */
const getStructureSeverity = (meta: string | RuleMeta | null | undefined): TagSeverity => {
  const parsed = parseMeta(meta);
  if (parsed?.commission_structure?.type === 'tiered') return 'info';
  if (parsed?.commission_structure?.type === 'progressive') return 'warning';
  return 'secondary';
};

/**
 * Format amount based on rule type
 * PRESERVED: bracket.rate * 100 to display as percent; rule.amount * 100 for simple commission.
 */
const formatAmount = (rule: PaymentRule): string => {
  const ruleType = rule.rule_type;
  const meta = rule.meta;

  // Check for commission structure brackets
  const parsed = parseMeta(meta);
  if (parsed?.commission_structure?.brackets) {
    const brackets = parsed.commission_structure.brackets;
    const summary = brackets.map(b => `${(b.rate * 100).toFixed(0)}%`).join(' → ');
    return summary;
  }

  // Commission - show as percentage
  if (ruleType && ruleType.includes('commission')) {
    return `${(Number(rule.amount) * 100).toFixed(0)}%`;
  }

  // Fixed amount - show as currency
  return n(parseFloat(String(rule.amount ?? 0)) || 0, 'currency');
};

/**
 * Load rules data
 */
const loadData = async (): Promise<void> => {
  isLoading.value = true;
  log('PaymentRulesSummaryModal', 'loadData', 'Loading rules...');

  try {
    const result = await listRules() as PaymentRule[];

    // Normalize active field and filter out inactive rules
    const allRules: PaymentRule[] = result.map(rule => ({
      ...rule,
      active: rule.active === 1 || rule.active === '1' || rule.active === true
    }));

    // Only show active rules
    rules.value = allRules.filter(rule => rule.active);

    log('PaymentRulesSummaryModal', 'loadData', 'Rules loaded', {
      totalRules: result.length,
      activeRules: rules.value.length
    });
  } catch (error) {
    log('PaymentRulesSummaryModal', 'loadData', 'Error loading rules', error);
    eventBus.emit('globalMessage.error', {
      message: t('Falha ao carregar regras de pagamento'),
      duration: 5000
    });
    rules.value = [];
  } finally {
    isLoading.value = false;
  }
};

/**
 * Open modal handler
 */
const handleOpen = async (): Promise<void> => {
  log('PaymentRulesSummaryModal', 'handleOpen', 'Opening modal');
  isVisible.value = true;
  await loadData();
};

/**
 * Close modal
 */
const closeModal = (): void => {
  log('PaymentRulesSummaryModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  rules.value = [];
};

// Event listeners
onMounted(() => {
  log('PaymentRulesSummaryModal', 'onMounted', 'Modal mounted');
  eventBus.on('modal.payment-rules-summary.open', handleOpen);
  eventBus.on('modal.payment-rules-summary.close', closeModal);
});

onBeforeUnmount(() => {
  log('PaymentRulesSummaryModal', 'onBeforeUnmount', 'Modal unmounting');
  eventBus.off('modal.payment-rules-summary.open', handleOpen);
  eventBus.off('modal.payment-rules-summary.close', closeModal);
});
</script>

<style scoped>
.summary-modal-content {
  min-height: 200px;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  color: var(--text-color-secondary);
}

/* Summary Header */
.summary-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  padding: 1rem 1.5rem;
  background: var(--surface-50);
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.summary-stat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.summary-stat i {
  font-size: 1.25rem;
  color: var(--primary-500);
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
}

.stat-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.summary-divider {
  width: 1px;
  height: 2rem;
  background: var(--surface-300);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
  color: var(--text-color-secondary);
}

.empty-state i {
  font-size: 3rem;
  color: var(--surface-400);
  margin-bottom: 1rem;
}

.empty-state h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  color: var(--text-color);
}

.empty-state p {
  margin: 0;
  max-width: 400px;
  font-size: 0.875rem;
}

/* Colaboradores List */
.colaboradores-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Colaborador Card */
.colaborador-card {
  background: white;
  border: 1px solid var(--surface-200);
  border-radius: 8px;
  overflow: hidden;
}

.colaborador-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-200);
}

.colaborador-avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-100);
  color: var(--primary-600);
  border-radius: 50%;
  font-size: 1.125rem;
}

.colaborador-info {
  display: flex;
  flex-direction: column;
}

.colaborador-name {
  font-weight: 600;
  font-size: 1rem;
  color: var(--text-color);
}

.colaborador-meta {
  font-size: 0.8125rem;
  color: var(--text-color-secondary);
}

/* Rules List */
.rules-list {
  padding: 0.5rem 0;
}

.rule-item {
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid var(--surface-100);
}

.rule-item:last-child {
  border-bottom: none;
}

.rule-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.375rem;
}

.rule-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
}

.rule-type i {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-50);
  color: var(--primary-600);
  border-radius: 6px;
  font-size: 0.875rem;
}

.rule-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--primary-600);
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

.rule-details {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.8125rem;
  color: var(--text-color-secondary);
  padding-left: 2.25rem;
}

.rule-franchise {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.rule-franchise i {
  font-size: 0.75rem;
}

.rule-frequency {
  color: var(--text-color-secondary);
}

.structure-tag {
  font-size: 0.6875rem;
  padding: 0.125rem 0.5rem;
}

/* Responsive */
@media (max-width: 768px) {
  .summary-header {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
  }

  .summary-divider {
    width: 100%;
    height: 1px;
  }

  .colaborador-header {
    padding: 0.875rem 1rem;
  }

  .rule-item {
    padding: 0.75rem 1rem;
  }

  .rule-details {
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-left: 0;
  }

  .rule-type i {
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
  }
}
</style>
